import { Transition } from "@uirouter/react";
import styled from "styled-components";
import { CreditBalance } from "../../components/header/credit-balance";
import { hiddenDesktop } from "../../components/layout";
import { MenuNode, MenuRoute } from "../../components/menu";
import { BillingDTO } from "../../store/billing/models/dto";
import { BillingService } from "../../store/billing/service";
import { LeafRole, Or } from "../../utils/and-or-tree";
import { OverlayRoutes } from "../overlay/routes";
import { ShellRouteNames } from "../shell/route-names";

import { AdminTransactionsAll } from "./admin-transactions/all";
import { Cart } from "./purchase";
import { PriceList } from "./purchase/prices";
import { ResultPage } from "./result";
import { BillingSummaryPage } from "./summary";
import { TransactionsAll } from "./transactions/all";
import { SingleTransaction } from "./transactions/single";
import { UsagesAll } from "./usages/all";
import { SingleUsage } from "./usages/single";

const StyledCreditBalance = styled(CreditBalance)`
  ${hiddenDesktop};
  margin-left: 0.5em;
`;

const names = {
  summary: `${ShellRouteNames.shell}.billing-summary`,
  purchase: `${ShellRouteNames.shell}.billing-purchase`,
  pricing: `${OverlayRoutes.name}.billing-pricing`,
  result: `${ShellRouteNames.shell}.billing-result`,
  allTransactions: `${ShellRouteNames.shell}.billing-all-tx`,
  adminAllTransactions: `${ShellRouteNames.shell}.admin-billing-all-tx`,
  singleTransaction: `${ShellRouteNames.shell}.billing-single-tx`,
  allUsages: `${ShellRouteNames.shell}.billing-all-usages`,
  singleUsages: `${ShellRouteNames.shell}.billing-single-usages`,
};

const states: Array<MenuRoute> = [
  {
    name: names.summary,
    component: BillingSummaryPage,
    url: "/billing",
    data: {
      title: "summary",
    },
  },
  {
    name: names.purchase,
    component: Cart,
    url: "/billing/purchase",
    data: {
      title: "purchase-credits",
    },
  },
  {
    name: names.pricing,
    component: PriceList,
    url: "/billing/purchase/pricing",
    data: {
      title: "pricing",
    },
  },
  {
    name: names.allTransactions,
    component: TransactionsAll,
    url: "/billing/transactions",
    data: {
      title: "transactions",
      roles: new Or(new LeafRole("ROLE_USER"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.allUsages,
    component: UsagesAll,
    url: "/billing/usage",
    data: {
      title: "usage",
    },
  },
  {
    name: names.result,
    component: ResultPage,
    data: {
      title: "purchase-result",
    },
    params: {
      transactionHash: {
        type: "string",
      },
      purchaseID: {
        type: "string",
        defaultValue: "",
      },
      error: {
        type: null,
        defaultValue: null,
      },
    },
    resolve: [
      {
        token: "transactionHash",
        deps: [Transition],
        resolveFn: (transition: Transition) =>
          transition.params().transactionHash,
      },
      {
        token: "purchaseID",
        deps: [Transition],
        resolveFn: (transition: Transition) => transition.params().purchaseID,
      },
      {
        token: "error",
        deps: [Transition],
        resolveFn: (transition: Transition) => transition.params().error,
      },
    ],
  },
  {
    name: names.singleTransaction,
    component: SingleTransaction,
    url: "/billing/transactions/:id",
    data: {
      title: "transaction-detail",
    },
    params: {
      id: {
        type: "string",
      },
    },
    resolve: [
      {
        token: "id",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (trans: Transition) => trans.params().id,
      },
      {
        token: "item",
        deps: ["id"],
        resolveFn: (id: string) =>
          BillingService.getSingleTransaction(id).catch((err) => err),
      },
    ],
    redirectTo: (trans: Transition) => {
      // getAsync tells the resolve to load
      let resolvePromise: Promise<
        BillingDTO.Responses.GetSingleTransaction.T | Error
      > = trans.injector().getAsync("item");
      return resolvePromise.then((
        err // TODO: maybe redirect to 404
      ) => (err instanceof Error ? names.allTransactions : undefined));
    },
  },
  {
    name: names.singleUsages,
    component: SingleUsage,
    url: "/billing/usages/:id",
    data: {
      title: "usage-detail",
    },
    params: {
      id: {
        type: "string",
      },
    },
    resolve: [
      {
        token: "id",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (trans: Transition) => trans.params().id,
      },
      {
        token: "item",
        deps: ["id"],
        resolveFn: (id: string) =>
          BillingService.getSingleUsage(id).catch((err) => err),
      },
    ],
    redirectTo: (trans: Transition) => {
      // getAsync tells the resolve to load
      let resolvePromise: Promise<
        BillingDTO.Responses.GetSingleUsage.T | Error
      > = trans.injector().getAsync("item");
      return resolvePromise.then((
        err // TODO: maybe redirect to 404
      ) => (err instanceof Error ? names.allUsages : undefined));
    },
  },
  {
    name: names.adminAllTransactions,
    component: AdminTransactionsAll,
    url: "/billing/transactions",
    data: {
      title: "purchases",
      roles: new Or(
        new LeafRole("ROLE_ADMIN"),
        new LeafRole("ROLE_COMMUNITY_MANAGER")
      ),
    },
  },
];

const sidebar: MenuNode.MenuNode = {
  icon: "cart",
  title: "billing",
  children: [
    {
      icon: "billing",
      route: states[0],
    },
    {
      icon: "card",
      route: states[1],
    },
    {
      icon: "transactions",
      route: states[2],
    },
    {
      icon: "usages",
      route: states[3],
    },
  ],
  roles: new Or(new LeafRole("ROLE_MERCHANT")),
  renderTitle: ({ title }) => (
    <>
      {title}
      <StyledCreditBalance />
    </>
  ),
};

const adminSidebar: MenuNode.MenuNode = {
  icon: "transactions",
  route: states[8],
  roles: new LeafRole("ROLE_ADMIN"),
};

export const BillingRoutes = {
  names,
  states,
  sidebar,
  adminSidebar,
};
